/**
 * @file releaseHighlighter.ts
 * @module app/utils/releaseHighlighter
 * @description Release tour and product walkthrough bootstrap utility for @inandi/release-highlighter.
 * Handles manifest loading, target element detection with retry logic, concurrent execution prevention,
 * and lifecycle management. Automatically detects late-rendered elements via polling and DOM mutation
 * observation, ensuring tour steps are shown when elements become available. Supports cookie-based
 * persistence across page navigations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.5
 * @version 1.2.5
 */

import { ReleaseHighlighter } from "@inandi/release-highlighter";

const RELEASE_MANIFEST_PATH = "/release.json";
const RELEASE_HIGHLIGHTER_BASE_OPTIONS = {
  classPrefix: "release-highlighter--",
};

type BootstrapReleaseHighlighterOptions = {
  force?: boolean;
};

type ReleaseManifestStep = {
  target?: string;
  targetClass?: string;
};

let cachedManifestSteps: ReleaseManifestStep[] | null = null;
let manifestLoadPromise: Promise<ReleaseManifestStep[]> | null = null;
let normalJourneyPromise: Promise<any> | null = null;
let forcedJourneyPromise: Promise<any> | null = null;
let isStarting = false;

const loadManifestSteps = async (): Promise<ReleaseManifestStep[]> => {
  if (cachedManifestSteps) {
    return cachedManifestSteps;
  }

  if (!manifestLoadPromise) {
    manifestLoadPromise = fetch(RELEASE_MANIFEST_PATH, { cache: "no-store" })
      .then((response) => response.json())
      .then((manifestJson) => {
        const steps = Array.isArray(manifestJson)
          ? manifestJson
          : manifestJson?.steps || [];
        cachedManifestSteps = steps;
        return steps;
      })
      .catch((error) => {
        manifestLoadPromise = null;
        throw error;
      });
  }

  return manifestLoadPromise;
};

const getJourney = async (force: boolean) => {
  const currentPromise = force ? forcedJourneyPromise : normalJourneyPromise;
  if (currentPromise) {
    return currentPromise;
  }

  const createdPromise = ReleaseHighlighter.fromJson(RELEASE_MANIFEST_PATH, {
    ...RELEASE_HIGHLIGHTER_BASE_OPTIONS,
    force,
  });

  if (force) {
    forcedJourneyPromise = createdPromise;
  } else {
    normalJourneyPromise = createdPromise;
  }

  return createdPromise;
};

const hasVisibleTargetForCurrentPage = (steps: ReleaseManifestStep[]) => {
  const selectors = steps
    .map((step) => {
      if (typeof step?.target === "string" && step.target.length > 0) {
        return step.target;
      }

      if (
        typeof step?.targetClass === "string" &&
        step.targetClass.length > 0
      ) {
        return `.${step.targetClass}`;
      }

      return null;
    })
    .filter((selector): selector is string => {
      return typeof selector === "string" && selector.length > 0;
    });

  if (!selectors.length) {
    return false;
  }

  return selectors.some((selector) => {
    return document.querySelector(selector);
  });
};

/**
 * Bootstraps the Release Highlighter tour on page load.
 * Handles retry logic for late-rendered elements and prevents concurrent starts.
 * Returns a cleanup function for unmounting.
 */
export const bootstrapReleaseHighlighter = (
  options: BootstrapReleaseHighlighterOptions = {}
) => {
  const force = options.force ?? false;
  let cancelled = false;

  const startTimer = window.setTimeout(async () => {
    if (cancelled || isStarting) return;

    isStarting = true;

    try {
      const manifestSteps = await loadManifestSteps();

      if (cancelled || !hasVisibleTargetForCurrentPage(manifestSteps)) {
        return;
      }

      const journey = await getJourney(force);
      if (cancelled) return;

      await journey.start();
    } catch (error) {
      console.error("Release highlighter start failed:", error);
    } finally {
      isStarting = false;
    }
  }, 250);

  return () => {
    cancelled = true;
    window.clearTimeout(startTimer);
  };
};

/**
 * Initializes the global Release Highlighter system.
 * Sets up all event listeners and triggers for the entire app.
 * Call this once in your main App component.
 *
 * @param {string} pathname - Current pathname from useLocation()
 * @param {string} search - Current search from useLocation()
 * @param {string} hash - Current hash from useLocation()
 * @returns {() => void} Cleanup function to remove all listeners
 *
 * @example
 * useEffect(() => {
 *   return initializeReleaseHighlighterSystem(pathname, search, hash);
 * }, [pathname, search, hash]);
 */
export const initializeReleaseHighlighterSystem = (
  pathname: string,
  search: string,
  hash: string
) => {
  const cleanupFunctions: Array<() => void> = [];

  // 1. Trigger on route change with aggressive retry logic
  const triggerWithRetry = (label: string) => {
    const retryAttempts = [0, 300, 700, 1200, 1800];
    const timers: NodeJS.Timeout[] = [];

    retryAttempts.forEach((delay) => {
      const timer = setTimeout(() => {
        bootstrapReleaseHighlighter({ force: false });
      }, delay);
      timers.push(timer);
    });

    const cleanup = () => {
      timers.forEach((timer) => clearTimeout(timer));
    };

    cleanupFunctions.push(cleanup);
    return cleanup;
  };

  // Initial trigger on route change
  triggerWithRetry("route-change");

  // 2. Handle tab/pill clicks
  const handleUiNavigationClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const triggerElement = target.closest(
      "[data-bs-toggle='tab'], [data-bs-toggle='pill'], [role='tab'], .nav-tabs .nav-link, .orders-main-tab-link"
    );

    if (!triggerElement) return;

    triggerWithRetry("tab-click");
  };

  // 3. Handle Bootstrap tab/pill shown events
  const handleBootstrapShown = () => {
    triggerWithRetry("bootstrap-shown");
  };

  // Attach event listeners
  window.addEventListener("click", handleUiNavigationClick);
  document.addEventListener("shown.bs.tab", handleBootstrapShown as EventListener);
  document.addEventListener("shown.bs.pill", handleBootstrapShown as EventListener);

  // Return cleanup function that removes all listeners and cancels pending timers
  return () => {
    window.removeEventListener("click", handleUiNavigationClick);
    document.removeEventListener("shown.bs.tab", handleBootstrapShown as EventListener);
    document.removeEventListener("shown.bs.pill", handleBootstrapShown as EventListener);
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
};
